import { differenceInDays, eachDayOfInterval, startOfYesterday } from 'date-fns';
import * as Dfns from 'date-fns/fp';
import * as uuid from 'uuid';
import {
  greaterThanIgnoreTime,
  lessThanIgnoreTime
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { convertDateToUTC, formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  AddMMMOfflineInputArgs,
  ControlVariableEntry,
  ControlVariableTable,
  CrossChannelMediaIds,
  DeleteMMMOfflineInputArgs,
  GetMMMOfflineInputsResponse,
  IncrementalityTestingEntry,
  IncrementalityTestingTable,
  MediaEntry,
  MediaTable,
  MenuItem,
  OfflineInputsEntry,
  OfflineInputsTable,
  OfflineInputTabs,
  OutcomeDataEntry,
  OutcomeDataTable,
  PromoData,
  TimelineEntry,
  UpdateControlVariablesArgs,
  UpdateIncrementalityTestsArgs,
  UpdateInputsArgs,
  UpdateMediaInputsArgs,
  UpdateMMMOfflineInputsArgs,
  UpdateMMMOfflineInputsResponse,
  UpdateOutcomeDataArgs
} from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { SetError } from '../../../redux/modals';
import {
  getSegmentName,
  getSegmentValue,
  CustomSegmentsData
} from '../../SegmentationMapping/segmentationMappingUtils';
import { exportMultiSheetToExcel, exportToExcel, Sheet } from '../../../utils/download-utils';
import { coerceNumericFields } from './exportNumberUtils';
import {
  awaitJSON,
  fetchWithCometFallback,
  MetricsLambdaFetch,
  SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
} from '../../../utils/fetch-utils';

export const isValidDmaValue = (value: string, validCodes: Set<string>): boolean =>
  value === 'canada' || validCodes.has(value.trim());

export interface TablesByTab {
  [OfflineInputTabs.PAID_MEDIA_INPUTS]: OfflineInputsTable[];
  [OfflineInputTabs.NON_PAID_MEDIA_INPUTS]: OfflineInputsTable[];
  [OfflineInputTabs.CONTROL_VARIABLES]: OfflineInputsTable[];
  [OfflineInputTabs.OUTCOME_DATA]: OfflineInputsTable[];
  [OfflineInputTabs.INCREMENTALITY_TESTING]: IncrementalityTestingTable[];
}

export interface QueuedRequest {
  type: 'CREATE' | 'DELETE';
  body: AddMMMOfflineInputArgs | DeleteMMMOfflineInputArgs;
}

export const OrderByOptions = {
  ALPHABETICAL: 'Alphabetical',
  DATE_ASCENDING: 'Date'
} as const;
export type OrderByOptions = (typeof OrderByOptions)[keyof typeof OrderByOptions];

export const ControlVariableTypes = {
  BINARY: 'binary',
  CONTINUOUS: 'continuous'
} as const;
export type ControlVariableTypes = (typeof ControlVariableTypes)[keyof typeof ControlVariableTypes];

export const ControlVariableUnitTypes = {
  BINARY: 'binary',
  PERCENTAGE: 'percentage',
  OTHER: 'other'
} as const;
export type ControlVariableUnitTypes =
  (typeof ControlVariableUnitTypes)[keyof typeof ControlVariableUnitTypes];

export const ControlVariableAggregationTypes = {
  SUM: 'sum',
  AVERAGE: 'average',
  MAX: 'max'
} as const;
export type ControlVariableAggregationTypes =
  (typeof ControlVariableAggregationTypes)[keyof typeof ControlVariableAggregationTypes];

export const IncrementalityTestExperimentTypes = {
  GEO: 'Geo Test',
  USER: 'User Test'
} as const;
export type IncrementalityTestExperimentTypes =
  (typeof IncrementalityTestExperimentTypes)[keyof typeof IncrementalityTestExperimentTypes];

export const IncrementalityTestExperimentLocations = {
  US: 'United States',
  CA: 'Canada',
  EU: 'Europe'
} as const;
/* eslint-disable @stylistic/max-len */
export type IncrementalityTestExperimentLocations =
  (typeof IncrementalityTestExperimentLocations)[keyof typeof IncrementalityTestExperimentLocations];
/* eslint-enable @stylistic/max-len */

export const IncrementalityTestPortionTypes = {
  TOTAL: 'Total',
  SUBSET: 'Subset'
} as const;
export type IncrementalityTestPortionTypes =
  (typeof IncrementalityTestPortionTypes)[keyof typeof IncrementalityTestPortionTypes];

export const IncrementalityTestMediaIdTypes = {
  ACCOUNT_ID: 'Account ID',
  CAMPAIGN_ID: 'Campaign ID'
} as const;
export type IncrementalityTestMediaIdTypes =
  (typeof IncrementalityTestMediaIdTypes)[keyof typeof IncrementalityTestMediaIdTypes];

export const IncrementalityTestKpiType = {
  REVENUE: 'revenue',
  VOLUME: 'volume'
} as const;
export type IncrementalityTestKpiType =
  (typeof IncrementalityTestKpiType)[keyof typeof IncrementalityTestKpiType];

export interface IncrementalityTestKpi {
  uuid: string;
  type: IncrementalityTestKpiType;
  name: string;
}

export const RequiredSegmentIds = {
  PLATFORM: '1',
  CHANNEL: '2'
} as const;
export type RequiredSegmentIds = (typeof RequiredSegmentIds)[keyof typeof RequiredSegmentIds];

// A client-side-only identity for a media entry row, used only by hasDma tables so that
// multiple rows can share a date (one per DMA). Never sent to the backend — the backend's
// real identity for a row is (table_id, date, dma), enforced by a DB unique index.
export interface MediaEntryWithRowId extends MediaEntry {
  rowId: string;
}

export const hasRowId = (entry: OfflineInputsEntry): entry is MediaEntryWithRowId =>
  typeof (entry as MediaEntryWithRowId).rowId === 'string';

export const ensureRowId = (entry: MediaEntry): MediaEntryWithRowId => {
  if (hasRowId(entry)) {
    return entry;
  }
  const withRowId = entry as MediaEntryWithRowId;
  withRowId.rowId = uuid.v4();
  return withRowId;
};

// A single unset row for a hasDma table to type or paste into. Not tied to any date —
// InputTable keeps exactly one of these around per hasDma table while editing.
export const buildBlankDmaEntryRow = (): MediaEntryWithRowId =>
  ensureRowId({
    date: '',
    impressions: undefined,
    otherUnit: undefined,
    redemptions: undefined,
    spend: undefined,
    dma: undefined
  });

// Parses a clipboard paste (tab-separated columns, newline-separated rows — the format
// Excel/Sheets copy already produces) into brand-new media entry rows, each with its own
// rowId. Mirrors the numeric-cell handling of the existing single-cell paste in
// InputTable.tsx (blank -> 0, strip non [\d.] characters) but treats every line as a new
// row rather than overwriting an existing one, and — uniquely for hasDma tables — reads the
// date out of the pasted text itself instead of assuming a pre-existing row's date.
export const parsePastedMediaEntryRows = (
  pastedText: string,
  columnsOrder: string[]
): MediaEntryWithRowId[] =>
  pastedText
    .split('\n')
    .filter(line => line.trim() !== '')
    .map(line => {
      const cells = line.split('\t');
      const entry: MediaEntry = { date: '' };

      columnsOrder.forEach((colName, index) => {
        const rawCell = (cells[index] ?? '').trim();

        if (colName === 'date') {
          entry.date = rawCell;
        } else if (colName === 'dma') {
          entry.dma = rawCell || undefined;
        } else {
          const filteredInput = rawCell.replaceAll(/[^\d.]/g, '');
          const parsedNumber = rawCell === '' ? 0 : Number(filteredInput);
          if (!Number.isNaN(parsedNumber)) {
            entry[colName as 'impressions' | 'redemptions' | 'spend' | 'otherUnit'] = parsedNumber;
          }
        }
      });

      return ensureRowId(entry);
    });

// Mutates every entry of every hasDma table in place, giving each a stable rowId. No-op for
// entries that already have one; untouched for non-hasDma tables. Call whenever tables are
// loaded/refreshed from the server, before any edit state is built on top of them.
export const assignRowIdsToMediaTables = (tables: MediaTable[]): MediaTable[] => {
  tables.filter(table => table.hasDma).forEach(table => table.entries.forEach(ensureRowId));
  return tables;
};

// Normalizes a raw date string (already-ISO, or a common human-entered format like
// "06/05/2026") to a UTC ISO string, or undefined if it can't be parsed as a valid date.
// Used to validate/canonicalize dates typed or pasted into a hasDma table's editable Date
// column, which — unlike every other table's Date column — is free text, not auto-generated.
export const normalizeDateString = (raw: string | undefined): string | undefined => {
  if (!raw || !raw.trim()) {
    return undefined;
  }
  const trimmed = raw.trim();
  const isoParsed = Dfns.parseISO(trimmed);
  const isoValid = Dfns.isValid(isoParsed);
  const parsed = isoValid ? isoParsed : new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  // If the input was an explicit ISO string with timezone info (ends with Z or +/-HH:MM offset),
  // use UTC getters to preserve the exact calendar date in UTC.
  // Otherwise (human-entered formats like MM/DD/YYYY or bare YYYY-MM-DD), use local getters
  // because new Date() interprets those as local midnight.
  const hasExplicitTimezone = /[Z]$|[+-]\d{2}:\d{2}$/.test(trimmed);
  if (hasExplicitTimezone) {
    return new Date(
      Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())
    ).toISOString();
  }
  return new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
  ).toISOString();
};

export interface DmaConflictGroup {
  tableId: string;
  date: string;
  dma: string;
  entries: MediaEntry[];
}

const dmaConflictKey = (entry: MediaEntry): string | undefined => {
  if (!entry.date || !entry.dma || !entry.dma.trim()) {
    return undefined;
  }
  const normalizedDate = normalizeDateString(entry.date) ?? entry.date;
  return `${normalizedDate}::${entry.dma.trim().toLowerCase()}`;
};

// Finds every (date, dma) pair that would land more than once in a hasDma table once
// tableEdits is saved — whether both rows are brand new, or a new row collides with an
// already-committed one. Editing an existing row in place (same rowId) is never a conflict
// with its own prior committed version, since it replaces rather than duplicates it.
export const findDmaConflicts = (
  mediaInputTables: MediaTable[],
  tableEdits: TableEditsMap
): DmaConflictGroup[] => {
  const conflicts: DmaConflictGroup[] = [];

  mediaInputTables
    .filter(table => table.hasDma)
    .forEach(table => {
      const edits = (tableEdits[table.tableId] ?? {}) as Record<string, MediaEntry>;
      const existingRowIds = new Set(table.entries.filter(hasRowId).map(entry => entry.rowId));

      const effectiveRows: MediaEntry[] = [
        ...table.entries.map(entry =>
          hasRowId(entry) && edits[entry.rowId] ? edits[entry.rowId] : entry
        ),
        ...Object.entries(edits)
          .filter(([rowId]) => !existingRowIds.has(rowId))
          .map(([, entry]) => entry)
      ];

      const groups = new Map<string, MediaEntry[]>();
      effectiveRows.forEach(entry => {
        const key = dmaConflictKey(entry);
        if (!key) {
          return;
        }
        groups.set(key, [...(groups.get(key) ?? []), entry]);
      });

      groups.forEach((entries, key) => {
        if (entries.length > 1) {
          const [date, dma] = key.split('::');
          conflicts.push({ tableId: table.tableId, date, dma, entries });
        }
      });
    });

  return conflicts;
};

export const formatDmaConflictMessage = (conflicts: DmaConflictGroup[]): string =>
  conflicts
    .map(({ date, dma, entries }) => {
      const dateLabel = date.split('T')[0];
      const values = entries.map(entry => formatMoney(entry.spend ?? 0)).join(' vs ');
      return `${dma} on ${dateLabel} has ${entries.length} rows (${values}) — remove or fix one before saving.`;
    })
    .join('\n');

const buildBlankDates = (startDate: Date, endDate?: Date): Date[] => {
  const startDateUTC = convertDateToUTC(startDate);
  const endDateUTC = endDate ? convertDateToUTC(endDate) : startOfYesterday();

  if (!(startDateUTC <= startOfYesterday())) {
    return [];
  }

  const blanks = eachDayOfInterval({
    start: startDateUTC,
    end: endDateUTC
  });

  return blanks;
};

export const buildBlankMediaEntries = (startDate: Date, endDate?: Date): MediaEntry[] => {
  const blanks = buildBlankDates(startDate, endDate);

  return [
    ...blanks.map(date => ({
      date: date.toISOString(),
      impressions: undefined,
      otherUnits: undefined,
      redemptions: undefined,
      spend: undefined,
      dma: undefined
    }))
  ];
};

// What a hasDma table should start with when entering edit mode. Non-DMA tables keep the
// existing one-blank-row-per-calendar-day behavior; hasDma tables start with none — every
// row is added explicitly (by pasting or typing into the capture row InputTable maintains).
export const buildMediaTableEditModeBlanks = (
  table: MediaTable,
  latestEntry: Date
): MediaEntry[] => (table.hasDma ? [] : buildBlankMediaEntries(latestEntry));

// Outcome-data and control-variable blank rows share the same { date, volume } shape.
const buildBlankDateVolumeEntries = (startDate: Date, endDate?: Date) =>
  buildBlankDates(startDate, endDate).map(date => ({
    date: date.toISOString(),
    volume: undefined
  }));

export const buildBlankOutcomeDataEntries = (startDate: Date, endDate?: Date): OutcomeDataEntry[] =>
  buildBlankDateVolumeEntries(startDate, endDate);

export const buildBlankControlVariableEntries = (
  startDate: Date,
  endDate?: Date
): ControlVariableEntry[] => buildBlankDateVolumeEntries(startDate, endDate);

// Scroll a table row (id `${tableId}_${yyyy-mm-dd}`) into view, retrying until it mounts.
// Hoisted from the per-tab controllers (MediaInputs/ControlVariables/OutcomeData), which
// had byte-identical copies.
export const scrollToRow = (tableId: string, date: string): void => {
  const idToFind = `${tableId}_${date.split('T')[0]}`;
  const foundEl = document.getElementById(idToFind);

  if (foundEl) {
    foundEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  } else {
    setTimeout(scrollToRow, 200, tableId, date);
  }
};

export const buildBlankIncrementalityTestingEntries = (
  kpis: IncrementalityTestKpi[]
): IncrementalityTestingEntry[] =>
  kpis.map(kpi => ({
    entryId: kpi.uuid,
    kpiName: kpi.name,
    spend: undefined,
    incrementalVolume: undefined,
    nControl: undefined,
    nTreatment: undefined,
    lowerBound: undefined,
    upperBound: undefined,
    confidenceLevel: undefined
  }));

export type EntryEditsMap = Record<string, OfflineInputsEntry>;

export type TableEditsMap = Record<string, EntryEditsMap>;

export type NewTableEntriesMap = Record<string, OfflineInputsEntry[]>;

// Args for the metrics-updateMediaInputTable lambda, which persists per-table
// edits made via the edit-settings modal (segment values, additional description).
export interface UpdateMediaInputTableArgs {
  tableId: string;
  company: string;
  updatedBy: string;
  customSegmentsMap?: Record<string, number>;
  additionalDescription?: string;
  hasDma?: boolean;
}

// Pending table-level metadata edits to apply on commit (e.g., custom segment
// values or additional description on an existing media input).
export type TableMetadataEditsMap = Record<
  string,
  {
    segmentMap?: Record<string, number>;
    additionalDescription?: string;
    hasDma?: boolean;
  }
>;

export type IncrementalityTestingEntryEditsMap = Record<string, IncrementalityTestingEntry>;

export type IncrementalityTestingTableEditsMap = Record<string, IncrementalityTestingEntryEditsMap>;

export type IncrementalityTestingNewTableEntriesMap = Record<string, IncrementalityTestingEntry[]>;

const DMA_ENTRIES_PAGE_SIZE = 5000;

interface DmaEntriesPage {
  entries: MediaEntry[];
  total: number;
}

const fetchDmaEntriesPage = async (
  company: string,
  tableId: string,
  offset: number
): Promise<DmaEntriesPage> => {
  const res = await MetricsLambdaFetch('/getMMMOfflineInputsMediaEntries', {
    params: { company, tableId, offset, limit: DMA_ENTRIES_PAGE_SIZE }
  });
  return awaitJSON<DmaEntriesPage>(res);
};

// The initial load leaves DMA tables' entries empty (they can hold 100k+ rows), so page them in.
// The first page reports the total, so the remaining pages are fetched in parallel.
const fetchDmaEntries = async (company: string, tableId: string): Promise<MediaEntry[]> => {
  const first = await fetchDmaEntriesPage(company, tableId, 0);
  const pageCount = Math.ceil(first.total / DMA_ENTRIES_PAGE_SIZE);
  const offsets = Array.from(
    { length: Math.max(pageCount - 1, 0) },
    (_, i) => (i + 1) * DMA_ENTRIES_PAGE_SIZE
  );
  const rest = await Promise.all(
    offsets.map(offset => fetchDmaEntriesPage(company, tableId, offset))
  );
  return [first, ...rest].flatMap(page => page.entries);
};

// Returns whether the fetch succeeded so callers can distinguish "no tables for this company"
// from "the request failed" instead of rendering the same empty state for both.
export const fetchTables = async (
  company: string,
  setTablesByTab: React.Dispatch<React.SetStateAction<TablesByTab>>,
  setError: SetError,
  setUpdatedByTab: React.Dispatch<
    React.SetStateAction<Record<OfflineInputTabs, { by?: string; at?: string }>>
  >
): Promise<boolean> => {
  const testParams = {
    company
  };

  try {
    const res = await MetricsLambdaFetch('/getMMMOfflineInputs', {
      params: testParams
    });
    const resJSON = await awaitJSON<GetMMMOfflineInputsResponse>(res);
    const mediaTables = [
      ...(resJSON[OfflineInputTabs.PAID_MEDIA_INPUTS]?.tables ?? []),
      ...(resJSON[OfflineInputTabs.NON_PAID_MEDIA_INPUTS]?.tables ?? [])
    ];
    const dmaTables = mediaTables.filter(table => table.hasDma);
    const dmaEntriesById: Record<string, MediaEntry[]> = Object.fromEntries(
      await Promise.all(
        dmaTables.map(async table => [table.tableId, await fetchDmaEntries(company, table.tableId)])
      )
    );
    const withDmaEntries = (tables?: MediaTable[]) =>
      tables?.map(table =>
        table.hasDma ? { ...table, entries: dmaEntriesById[table.tableId] } : table
      );
    const updates = Object.assign(
      {},
      ...Object.values(OfflineInputTabs).map(tab => ({
        [tab]: { by: resJSON[tab]?.updatedBy, at: resJSON[tab]?.updatedAt }
      }))
    );
    const tables = Object.assign(
      {},
      ...Object.values(OfflineInputTabs).map(tab => ({
        [tab]:
          tab === OfflineInputTabs.PAID_MEDIA_INPUTS ||
          tab === OfflineInputTabs.NON_PAID_MEDIA_INPUTS ||
          tab === OfflineInputTabs.OUTCOME_DATA ||
          tab === OfflineInputTabs.CONTROL_VARIABLES ||
          tab === OfflineInputTabs.INCREMENTALITY_TESTING
            ? resJSON[tab]?.tables
            : []
      })),
      {
        [OfflineInputTabs.PAID_MEDIA_INPUTS]: withDmaEntries(
          resJSON[OfflineInputTabs.PAID_MEDIA_INPUTS]?.tables
        ),
        [OfflineInputTabs.NON_PAID_MEDIA_INPUTS]: withDmaEntries(
          resJSON[OfflineInputTabs.NON_PAID_MEDIA_INPUTS]?.tables
        )
      }
    );

    setTablesByTab(tables);
    setUpdatedByTab(updates);
    return true;
  } catch (e) {
    setError({
      message: e.message,
      reportError: e
    });
    return false;
  }
};

export const fetchCrossChannelMediaIds = async (
  company: string,
  setCrossChannelMediaIds: React.Dispatch<React.SetStateAction<CrossChannelMediaIds>>,
  setError: SetError
): Promise<void> => {
  try {
    const params = { company };
    const res = await MetricsLambdaFetch('/getCrossChannelMediaIds', {
      params
    });
    const resJSON = await awaitJSON<CrossChannelMediaIds>(res);
    setCrossChannelMediaIds(resJSON);
  } catch (e) {
    setError({
      message: `Failed to fetch Incrementality Testing Media Ids: ${e.message}`,
      reportError: e
    });
  }
};

export const fetchValidDmaCodes = async (setError: SetError): Promise<Set<string>> => {
  try {
    const res = await MetricsLambdaFetch('/getValidDmaCodes');
    const { dmaCodes } = await awaitJSON<{ dmaCodes: string[] }>(res);
    return new Set(dmaCodes);
  } catch (e) {
    setError({ message: `Failed to load DMA codes: ${e.message}`, reportError: e });
    return new Set();
  }
};

export const prepDeleteTableReq = <
  TTable extends { tableId: string },
  TTableEditsMap extends Record<string, any>,
  TNewTableEntriesMap extends Record<string, any>
>(
  tableId: string,
  company: string,
  updatedBy: string,
  inputType: keyof typeof OfflineInputTabs,
  tables: TTable[],
  setTables: React.Dispatch<React.SetStateAction<TTable[]>>,
  deletedTables: TTable[],
  setDeletedTables: React.Dispatch<React.SetStateAction<TTable[]>>,
  queuedRequests: QueuedRequest[],
  setQueuedRequests: React.Dispatch<React.SetStateAction<QueuedRequest[]>>,
  newTableEntries: TNewTableEntriesMap,
  setNewTableEntries: React.Dispatch<React.SetStateAction<TNewTableEntriesMap>>,
  tableEdits: TTableEditsMap,
  setTableEdits: React.Dispatch<React.SetStateAction<TTableEditsMap>>
): void => {
  const foundCreateIndex = queuedRequests.findIndex(req => {
    try {
      if (req.type === 'CREATE') {
        const createBody = req.body as AddMMMOfflineInputArgs;
        const createArgs = createBody.args;
        return createArgs.tableId === tableId;
      }
    } catch {
      /* Malformed queued request; treat as not found. */
    }
    return false;
  });

  // If the table is brand new and has not been sent to the server yet, then only modify the local state
  if (foundCreateIndex >= 0) {
    queuedRequests.splice(foundCreateIndex, 1);
    setQueuedRequests([...queuedRequests]);
  }

  const tableIndex = tables.findIndex(table => table.tableId === tableId);

  // Save the deleted table to be restored after discard
  if (foundCreateIndex === -1) {
    const deletedTable = tables[tableIndex];
    deletedTables.push(deletedTable);
    setDeletedTables([...deletedTables]);
  }

  // Modify local state as if delete were completed
  tables.splice(tableIndex, 1);
  setTables([...tables]);
  // Callers pass these maps together with their setters; mutating in place then
  // dispatching a copy is the intended contract of this helper.
  // eslint-disable-next-line no-param-reassign
  delete newTableEntries[tableId];
  setNewTableEntries({ ...newTableEntries });
  // eslint-disable-next-line no-param-reassign
  delete tableEdits[tableId];
  setTableEdits({ ...tableEdits });

  if (foundCreateIndex === -1) {
    setQueuedRequests([
      ...queuedRequests,
      {
        type: 'DELETE',
        body: { tableId, company, updatedBy, inputType }
      }
    ]);
  }
};

// Also called from validateAndUpdateTables' sequential queuedRequests loop (one request must
// complete before the next starts) — the short visibility-wait bound applies there; harmless for
// this function's other, one-off callers too.
export const deleteTable = async (
  body: DeleteMMMOfflineInputArgs,
  setError: SetError
): Promise<void> => {
  try {
    const res = await MetricsLambdaFetch('/deleteMMMOfflineInput', {
      method: 'POST',
      body,
      visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
    });
    await awaitJSON<boolean>(res);
  } catch (e) {
    setError({
      message: e.message,
      reportError: e
    });
  }
};

export const prepCreateTableReq = <
  TTable extends { tableId: string },
  TTableEditsMap extends Record<string, any>,
  TNewTableEntriesMap extends Record<string, any>
>(input: {
  company: string;
  updatedBy: string;
  inputType: keyof typeof OfflineInputTabs;
  startDate: Date;
  endDate?: Date;
  kpis?: IncrementalityTestKpi[];
  newTable: TTable;
  queuedRequests: QueuedRequest[];
  setQueuedRequests: React.Dispatch<React.SetStateAction<QueuedRequest[]>>;
  tables: TTable[];
  setTables: React.Dispatch<React.SetStateAction<TTable[]>>;
  tablesInEditMode: string[];
  setTablesInEditMode: React.Dispatch<React.SetStateAction<string[]>>;
  newTableEntries: TNewTableEntriesMap;
  setNewTableEntries: React.Dispatch<React.SetStateAction<TNewTableEntriesMap>>;
  tableEdits: TTableEditsMap;
  setTableEdits: React.Dispatch<React.SetStateAction<TTableEditsMap>>;
}): void => {
  const {
    company,
    updatedBy,
    inputType,
    startDate,
    endDate,
    kpis,
    newTable,
    queuedRequests,
    setQueuedRequests,
    tables,
    setTables,
    tablesInEditMode,
    setTablesInEditMode,
    newTableEntries,
    setNewTableEntries,
    tableEdits,
    setTableEdits
  } = input;

  // Narrows the generic `newTable` to a MediaTable without a cast — true only when
  // called for the media tabs below, where `newTable` is always a MediaTable at runtime.
  const isMediaTableInput = (table: {
    tableId: string;
  }): table is { tableId: string } & MediaTable => 'hasDma' in table;

  switch (inputType as OfflineInputTabs) {
    case OfflineInputTabs.PAID_MEDIA_INPUTS:
    case OfflineInputTabs.NON_PAID_MEDIA_INPUTS:
      (newTableEntries as Record<string, any>)[newTable.tableId] =
        isMediaTableInput(newTable) && newTable.hasDma ? [] : buildBlankMediaEntries(startDate);
      break;
    case OfflineInputTabs.OUTCOME_DATA:
      (newTableEntries as Record<string, any>)[newTable.tableId] = buildBlankOutcomeDataEntries(
        startDate,
        endDate
      );
      break;
    case OfflineInputTabs.CONTROL_VARIABLES:
      (newTableEntries as Record<string, any>)[newTable.tableId] = buildBlankControlVariableEntries(
        startDate,
        endDate
      );
      break;
    case OfflineInputTabs.INCREMENTALITY_TESTING:
      if (kpis !== undefined && kpis !== null) {
        (newTableEntries as Record<string, any>)[newTable.tableId] =
          buildBlankIncrementalityTestingEntries(kpis);
      }
      break;
    default:
      break;
  }
  setNewTableEntries({ ...newTableEntries });
  tables.unshift(newTable);
  setTables([...tables]);
  tablesInEditMode.push(newTable.tableId);
  setTablesInEditMode([...tablesInEditMode]);
  (tableEdits as Record<string, any>)[newTable.tableId] = {};
  setTableEdits({ ...tableEdits });

  const args: any = { ...newTable, company, updatedBy };
  delete args.entries;
  const body: AddMMMOfflineInputArgs = { inputType, args };

  setQueuedRequests([
    ...queuedRequests,
    {
      type: 'CREATE',
      body
    }
  ]);
};

// Also called from validateAndUpdateTables' sequential queuedRequests loop — see deleteTable's
// comment.
export const createTable = async (
  body: AddMMMOfflineInputArgs,
  setError: SetError
): Promise<void> => {
  try {
    await MetricsLambdaFetch('/addMMMOfflineInput', {
      method: 'POST',
      body,
      visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
    });
  } catch (e) {
    setError({
      message: e.message,
      reportError: e
    });
  }
};

const validateTableEdits = (
  tableEdits: TableEditsMap,
  newTableEntries: NewTableEntriesMap
): { tableId: string; errors: string[] }[] => {
  const validationErrors: { tableId: string; errors: string[] }[] = [];
  const combinedKeys = new Set([...Object.keys(tableEdits), ...Object.keys(newTableEntries)]);

  combinedKeys.forEach(tableId => {
    const entriesEdits: EntryEditsMap = tableEdits[tableId] ?? [];
    const newEntries: MediaEntry[] = newTableEntries[tableId] ?? [];
    const validationErrorsByDate: EntryEditsMap = {};

    // Find new dates with missing data. hasDma rows are keyed by rowId (not date) and are
    // already fully validated elsewhere (DMA-validity, invalid-date, and findDmaConflicts
    // checks in validateAndUpdateTables) — skip this date-completeness check for them.
    newEntries.forEach(newEntry => {
      if (hasRowId(newEntry)) {
        return;
      }

      const editAtDate = entriesEdits[newEntry.date];

      if (!editAtDate) {
        validationErrorsByDate[newEntry.date] = newEntry;
      }
    });

    const dates = Object.keys(validationErrorsByDate).sort();
    // Group adjacent dates where the earlier date is exactly one day before the next.
    const consecutiveErroringDates: string[][] = [];
    dates.forEach(date => {
      const currentGroup = consecutiveErroringDates[consecutiveErroringDates.length - 1];
      const prev = currentGroup?.[currentGroup.length - 1];
      if (prev !== undefined && differenceInDays(new Date(prev), new Date(date)) === -1) {
        currentGroup.push(date);
      } else {
        consecutiveErroringDates.push([date]);
      }
    });

    consecutiveErroringDates.forEach(consecutives => {
      validationErrors.push({ tableId, errors: consecutives });
    });
  });

  return validationErrors;
};

const validateIncrementalityTestingEntry = (entry: IncrementalityTestingEntry): boolean => {
  if (
    entry.entryId === undefined ||
    entry.entryId === null ||
    !entry.kpiName ||
    entry.spend === undefined ||
    entry.spend === null ||
    entry.incrementalVolume === undefined ||
    entry.incrementalVolume === null
  ) {
    return false;
  }
  return true;
};

const validateIncrementalityTestingTableEdits = (
  tableEdits: IncrementalityTestingTableEditsMap,
  newTableEntries: IncrementalityTestingNewTableEntriesMap
): { tableId: string; errors: string[] }[] => {
  const validationErrors: { tableId: string; errors: string[] }[] = [];
  const combinedKeys = new Set([...Object.keys(tableEdits), ...Object.keys(newTableEntries)]);

  combinedKeys.forEach(tableId => {
    const entriesEdits: IncrementalityTestingEntryEditsMap = tableEdits[tableId] ?? [];
    const newEntries: IncrementalityTestingEntry[] = newTableEntries[tableId] ?? [];
    const validationErrorsByEntryId: IncrementalityTestingEntryEditsMap = {};
    // find any invalid entries
    Object.entries(entriesEdits).forEach(([entryId, entry]) => {
      if (!validateIncrementalityTestingEntry(entry)) {
        validationErrorsByEntryId[entryId] = entry;
      }
    });
    // Find potentially missing entries
    newEntries.forEach(newEntry => {
      const editAtKpi = entriesEdits[newEntry.entryId];
      if (!editAtKpi) {
        validationErrorsByEntryId[newEntry.entryId] = newEntry;
      }
    });
    const errors = Object.values(validationErrorsByEntryId).map(entry => entry.entryId);
    if (errors.length) {
      validationErrors.push({ tableId, errors });
    }
  });

  return validationErrors;
};

export const UPDATE_CHUNK_ROW_LIMIT = 5000;

// Splits tables into request-sized groups of at most `limit` entries, splitting a table across
// groups when needed. Tables without entries still get sent once.
export const chunkUpdateTables = <T extends { entries: unknown[] }>(
  tables: T[],
  limit = UPDATE_CHUNK_ROW_LIMIT
): T[][] => {
  const chunks: T[][] = [];
  let current: T[] = [];
  let room = limit;
  for (const table of tables) {
    let offset = 0;
    do {
      const take = table.entries.slice(offset, offset + room);
      current.push({ ...table, entries: take });
      offset += take.length;
      room -= take.length;
      if (room === 0) {
        chunks.push(current);
        current = [];
        room = limit;
      }
    } while (offset < table.entries.length);
  }
  if (current.length > 0) {
    chunks.push(current);
  }
  return chunks;
};

// Adds one chunk's per-table row counts into the running totals.
export const addUpdateCounts = (
  totals: UpdateMMMOfflineInputsResponse,
  chunkResult: UpdateMMMOfflineInputsResponse
): UpdateMMMOfflineInputsResponse => {
  const next = { ...totals };
  for (const [tableId, count] of Object.entries(chunkResult)) {
    next[tableId] = (next[tableId] ?? 0) + count;
  }
  return next;
};

const countRows = (tables: { entries: unknown[] }[]): number =>
  tables.reduce((sum, table) => sum + table.entries.length, 0);

const updateTables = async (
  company: string,
  updatedBy: string,
  inputType: keyof typeof OfflineInputTabs,
  tableEdits: TableEditsMap | IncrementalityTestingTableEditsMap,
  setError: SetError,
  onSuccess?: (updatedTables: UpdateMMMOfflineInputsResponse) => void
): Promise<void> => {
  let args: UpdateInputsArgs = {
    company,
    updatedBy,
    tables: []
  };
  switch (inputType as OfflineInputTabs) {
    case OfflineInputTabs.PAID_MEDIA_INPUTS:
    case OfflineInputTabs.NON_PAID_MEDIA_INPUTS: {
      const mediaArgs: UpdateMediaInputsArgs = {
        tables: Object.keys(tableEdits).map(tableId => ({
          tableId,
          entries: Object.values((tableEdits as TableEditsMap)[tableId]).map(entry => {
            const mediaEntry = entry as MediaEntry;
            return {
              date: normalizeDateString(mediaEntry.date) ?? mediaEntry.date,
              // Leave untouched cells as null rather than defaulting to 0 -- econ needs to
              // distinguish "no data for this day" from "confirmed zero" (DEV-21622).
              impressions: mediaEntry.impressions,
              otherUnit: mediaEntry.otherUnit,
              redemptions: mediaEntry.redemptions,
              spend: mediaEntry.spend,
              dma: mediaEntry.dma?.trim() || undefined
            };
          })
        })),
        company,
        updatedBy
      };
      args = mediaArgs;
      break;
    }
    case OfflineInputTabs.OUTCOME_DATA: {
      const outcomeDataArgs: UpdateOutcomeDataArgs = {
        tables: Object.keys(tableEdits).map(key => ({
          tableId: key,
          entries: Object.values(tableEdits[key]).map(entry => {
            const outcomeDataEntry = entry as OutcomeDataEntry;
            return {
              date: outcomeDataEntry.date,
              volume: outcomeDataEntry.volume ?? 0
            };
          })
        })),
        company,
        updatedBy
      };
      args = outcomeDataArgs;
      break;
    }
    case OfflineInputTabs.CONTROL_VARIABLES: {
      const controlVariableArgs: UpdateControlVariablesArgs = {
        tables: Object.keys(tableEdits).map(key => ({
          tableId: key,
          entries: Object.values(tableEdits[key]).map(entry => {
            const controlVariableEntry = entry as ControlVariableEntry;
            return {
              date: controlVariableEntry.date,
              volume: controlVariableEntry.volume
            };
          })
        })),
        company,
        updatedBy
      };
      args = controlVariableArgs;
      break;
    }
    case OfflineInputTabs.INCREMENTALITY_TESTING: {
      const incrementalityTestingArgs: UpdateIncrementalityTestsArgs = {
        tables: Object.keys(tableEdits).map(key => ({
          tableId: key,
          entries: Object.values(tableEdits[key]).map(entry => {
            const incrementalityTestEntry = entry as IncrementalityTestingEntry;
            return {
              entryId: incrementalityTestEntry.entryId,
              kpiName: incrementalityTestEntry.kpiName,
              spend: incrementalityTestEntry.spend,
              incrementalVolume: incrementalityTestEntry.incrementalVolume,
              nControl: incrementalityTestEntry.nControl ?? undefined,
              nTreatment: incrementalityTestEntry.nTreatment ?? undefined,
              lowerBound: incrementalityTestEntry.lowerBound ?? undefined,
              upperBound: incrementalityTestEntry.upperBound ?? undefined,
              confidenceLevel: incrementalityTestEntry.confidenceLevel ?? undefined
            };
          })
        })),
        company,
        updatedBy
      };
      args = incrementalityTestingArgs;
      break;
    }
    default:
      break;
  }

  let savedRows = 0;
  const totalRows = countRows(args.tables);
  try {
    if (args.tables.length > 0) {
      // Large DMA tables exceed the request payload limits in a single call, so send bounded chunks
      // one after another. The backend upserts, so a retry after a partial failure is safe.
      let totals: UpdateMMMOfflineInputsResponse = {};
      const chunks = chunkUpdateTables(args.tables);
      for (const chunk of chunks) {
        const lambdaArgs: UpdateMMMOfflineInputsArgs = {
          inputType,
          args: { ...args, tables: chunk }
        };
        const lambdaResult = await fetchWithCometFallback<
          UpdateMMMOfflineInputsArgs,
          UpdateMMMOfflineInputsResponse
        >({
          service: MetricsLambdaFetch,
          standardEndpoint: '/updateMMMOfflineInputs',
          input: lambdaArgs,
          method: 'post'
        });
        totals = addUpdateCounts(totals, lambdaResult);
        savedRows += countRows(chunk);
      }

      if (onSuccess) {
        onSuccess(totals);
      }
    } else if (onSuccess) {
      onSuccess({});
    }
  } catch (e) {
    setError({
      message:
        savedRows > 0
          ? `${savedRows} of ${totalRows} rows saved before an error: ${e.message}. Retrying the upload is safe.`
          : e.message,
      reportError: e
    });
  }
};

// Also called from validateAndUpdateTables' sequential tableMetadataEdits loop — see deleteTable's
// comment.
export const updateMediaInputTable = async (
  args: UpdateMediaInputTableArgs,
  setError: SetError
): Promise<boolean> => {
  try {
    await MetricsLambdaFetch('/updateMediaInputTable', {
      method: 'POST',
      body: args,
      visibilityWaitTimeoutMs: SEQUENTIAL_VISIBILITY_WAIT_TIMEOUT_MS
    });
    return true;
  } catch (e) {
    setError({ message: e.message, reportError: e });
    return false;
  }
};

export const validateAndUpdateTables = async (
  company: string,
  updatedBy: string,
  inputType: keyof typeof OfflineInputTabs,
  tableEdits: TableEditsMap | IncrementalityTestingTableEditsMap,
  newTableEntries: NewTableEntriesMap | IncrementalityTestingNewTableEntriesMap,
  setValidationErrors: React.Dispatch<
    React.SetStateAction<{ tableId: string; errors: string[] }[]>
  >,
  setError: SetError,
  onSuccess: (updatedTables: UpdateMMMOfflineInputsResponse) => void,
  queuedRequests: QueuedRequest[],
  setQueuedRequests: React.Dispatch<React.SetStateAction<QueuedRequest[]>>,
  tableMetadataEdits?: TableMetadataEditsMap,
  mediaInputTables?: MediaTable[],
  validCodes: Set<string> = new Set()
): Promise<void> => {
  const invalidEntries =
    (inputType as OfflineInputTabs) === OfflineInputTabs.INCREMENTALITY_TESTING
      ? validateIncrementalityTestingTableEdits(
          tableEdits as IncrementalityTestingTableEditsMap,
          newTableEntries as IncrementalityTestingNewTableEntriesMap
        )
      : validateTableEdits(tableEdits as TableEditsMap, newTableEntries as NewTableEntriesMap);
  if (invalidEntries.length > 0) {
    setValidationErrors(invalidEntries);
  } else {
    // DMA validation: for tables with hasDma === true, every edited entry must have a valid DMA value.
    if (mediaInputTables) {
      const dmaTableIds = new Set(mediaInputTables.filter(t => t.hasDma).map(t => t.tableId));
      const editsMap = tableEdits as TableEditsMap;
      for (const tableId of Object.keys(editsMap)) {
        if (!dmaTableIds.has(tableId)) {
          continue;
        }
        for (const entry of Object.values(editsMap[tableId])) {
          const mediaEntry = entry as MediaEntry;
          if (!mediaEntry.dma || !isValidDmaValue(mediaEntry.dma, validCodes)) {
            setError({
              message: `Invalid or missing DMA value "${mediaEntry.dma ?? '(missing)'}" for date ${mediaEntry.date}. Must be a valid DMA code or "canada".`
            });
            return;
          }
          if (!normalizeDateString(mediaEntry.date)) {
            setError({
              message: `Invalid date "${mediaEntry.date}" for DMA ${mediaEntry.dma}. Please enter a valid date.`
            });
            return;
          }
        }
      }

      const dmaConflicts = findDmaConflicts(mediaInputTables, editsMap);
      if (dmaConflicts.length > 0) {
        setError({ message: formatDmaConflictMessage(dmaConflicts) });
        return;
      }
    }

    for (const req of queuedRequests) {
      switch (req.type) {
        case 'DELETE':
          await deleteTable(req.body as DeleteMMMOfflineInputArgs, setError);
          break;
        case 'CREATE':
          await createTable(req.body as AddMMMOfflineInputArgs, setError);
          break;
        default:
          break;
      }
    }

    setQueuedRequests([]);

    if (tableMetadataEdits) {
      for (const [tableId, edit] of Object.entries(tableMetadataEdits)) {
        await updateMediaInputTable(
          {
            company,
            updatedBy,
            tableId,
            customSegmentsMap: edit.segmentMap,
            additionalDescription: edit.additionalDescription,
            hasDma: edit.hasDma
          },
          setError
        );
      }
    }

    await updateTables(company, updatedBy, inputType, tableEdits, setError, onSuccess);
  }
};

const NUMERIC_EXPORT_FIELDS: Partial<Record<OfflineInputTabs, string[]>> = {
  [OfflineInputTabs.PAID_MEDIA_INPUTS]: ['impressions', 'redemptions', 'spend', 'otherUnit'],
  [OfflineInputTabs.NON_PAID_MEDIA_INPUTS]: ['impressions', 'redemptions', 'spend', 'otherUnit'],
  [OfflineInputTabs.OUTCOME_DATA]: ['volume'],
  [OfflineInputTabs.CONTROL_VARIABLES]: ['volume'],
  [OfflineInputTabs.INCREMENTALITY_TESTING]: [
    'spend',
    'incrementalVolume',
    'nControl',
    'nTreatment',
    'lowerBound',
    'upperBound',
    'confidenceLevel'
  ]
};

export const tabularOfflineInputsDownload = (
  inputType: keyof typeof OfflineInputTabs,
  tables: OfflineInputsTable[] | IncrementalityTestingTable[],
  customSegments?: CustomSegmentsData[]
): void => {
  const numericFields = NUMERIC_EXPORT_FIELDS[inputType] ?? [];
  const exportData: Sheet[] = [];
  for (const table of tables) {
    let sheetName: string;
    const { entries } = table;
    switch (inputType as OfflineInputTabs) {
      case OfflineInputTabs.OUTCOME_DATA: {
        const { kpiName, kpiDescription } = table as OutcomeDataTable;
        sheetName = kpiName.concat('|', kpiDescription || '');
        break;
      }
      case OfflineInputTabs.CONTROL_VARIABLES: {
        const { name, description } = table as ControlVariableTable;
        sheetName = name.concat('|', description || '');
        break;
      }
      case OfflineInputTabs.PAID_MEDIA_INPUTS:
      case OfflineInputTabs.NON_PAID_MEDIA_INPUTS: {
        const {
          channelId,
          platformId,
          segmentMap = {},
          additionalDescription
        } = table as MediaTable;
        const channelName = getSegmentValue(channelId, customSegments || []);
        const platformName = getSegmentValue(platformId, customSegments || []);

        let customSegmentsString = '';
        for (const segmentId of Object.keys(segmentMap ?? {})) {
          if (
            segmentId !== RequiredSegmentIds.CHANNEL &&
            segmentId !== RequiredSegmentIds.PLATFORM
          ) {
            const segmentName = getSegmentName(
              Number.parseInt(segmentId, 10),
              customSegments || []
            );
            const segmentValueName = getSegmentValue(segmentMap[segmentId], customSegments || []);
            customSegmentsString = customSegmentsString.concat(
              `|${segmentName}: ${segmentValueName}`
            );
          }
        }

        sheetName = channelName.concat(
          platformName ? ` | ${platformName}` : '',
          customSegmentsString ? ` | ${customSegmentsString}` : '',
          additionalDescription ? ` | ${additionalDescription}` : ''
        );

        exportData.push({
          name: sheetName.replace(/\|N\/A/g, ''),
          // `rowId` is a client-side-only identity that assignRowIdsToMediaTables adds to every
          // hasDma entry in place. The download util derives its column headers from the exported
          // objects' own keys, so leaving it in ships a stray column of UUIDs to the user. Drop it
          // unconditionally — it's a no-op for non-hasDma entries, which never have one.
          data: entries.map(entry => {
            const { rowId, ...entryWithoutRowId } = entry as MediaEntry & { rowId?: string };
            return coerceNumericFields(entryWithoutRowId, numericFields);
          })
        });
        continue;
      }
      case OfflineInputTabs.INCREMENTALITY_TESTING: {
        const { name, testType, startDate, endDate } = table as IncrementalityTestingTable;
        sheetName = name.concat('|', testType || '', '|', startDate || '', '|', endDate || '');
        break;
      }
      default: {
        return;
      }
    }
    sheetName = sheetName.replace(/\|N\/A/g, '');
    exportData.push({
      name: sheetName,
      data: entries.map(entry => coerceNumericFields(entry, numericFields))
    });
  }
  exportMultiSheetToExcel(exportData, inputType);
};

export const invalidOfflineInputsDates = (date: string): boolean => {
  const invalidFormat = !Dfns.isValid(Dfns.parseISO(date));
  if (invalidFormat) {
    return true;
  }
  const inputYear = Dfns.getYear(Dfns.parseISO(date));
  const outsideYearRange = inputYear < 2017;
  return outsideYearRange || invalidFormat;
};

export const fetchPromosData = async (
  company: string,
  setPromos: (promos: MenuItem[]) => void,
  setOffers: (offers: MenuItem[]) => void,
  setTimelineEntries: React.Dispatch<React.SetStateAction<TimelineEntry[]>>,
  setError: SetError
): Promise<void> => {
  const testParams = {
    company
  };
  try {
    const res = await MetricsLambdaFetch('/getOfflineInputsPromoData', {
      params: testParams
    });
    const resObject = await awaitJSON<PromoData>(res);

    setPromos(resObject.promos ?? []);
    setOffers(resObject.offers ?? []);
    setTimelineEntries(resObject.timeline);
  } catch (e) {
    setError({
      message: e.message,
      reportError: e
    });
  }
};

export const stageEntry = (
  newEntry: TimelineEntry,
  setStagedEntries: React.Dispatch<React.SetStateAction<TimelineEntry[]>>
): void => {
  setStagedEntries(prev => [...prev, newEntry]);
};

export const commitPromosChanges = async (
  stagedEntries: TimelineEntry[],
  stagedDeletes: TimelineEntry[],
  onCommitSuccess: () => void,
  setError: SetError,
  company: string
): Promise<void> => {
  const entriesToCommit = stagedEntries.filter(
    stagedEntry =>
      !stagedDeletes.some(stagedDelete => stagedDelete.promoPeriodId === stagedEntry.promoPeriodId)
  );

  const deletesToCommit = stagedDeletes.filter(
    stagedDelete =>
      !stagedEntries.some(stagedEntry => stagedEntry.promoPeriodId === stagedDelete.promoPeriodId)
  );
  try {
    if (deletesToCommit.length) {
      await MetricsLambdaFetch('/deleteOfflineInputsPromo', {
        method: 'DELETE',
        body: { entries: deletesToCommit, company }
      });
    }
    if (entriesToCommit.length) {
      await MetricsLambdaFetch('/createOfflineInputsPromo', {
        method: 'POST',
        body: { entries: entriesToCommit, company }
      });
    }
    onCommitSuccess();
  } catch (e) {
    setError({
      message: e.message,
      reportError: e
    });
  }
};

export const sortMenuItemsAlphabetically = (itemArr: MenuItem[]): MenuItem[] =>
  [...itemArr].sort((a, b) => a.label.localeCompare(b.label));

export const sortTimelineEntriesAlphabetically = (entries: TimelineEntry[]): TimelineEntry[] =>
  [...entries].sort((a, b) => a.promoName.localeCompare(b.promoName));

export const sortTimelineEntriesByDateAsc = (entries: TimelineEntry[]): TimelineEntry[] =>
  [...entries].sort((a, b) => (lessThanIgnoreTime(a.startDate, b.startDate, true) ? -1 : 1));

export const isValidTimelineEntry = (entry: TimelineEntry, dateMin: Date, dateMax: Date): boolean =>
  !(
    greaterThanIgnoreTime(entry.startDate, dateMax, false) ||
    lessThanIgnoreTime(entry.endDate, dateMin, false)
  );

// Promo rows saved without a promo period ID come back from getOfflineInputsPromoData
// merged into a single entry with no ID (and often no name) -- e.g. away's 189 unnamed
// outlier-event days, 2022-2025. The timeline chart keys its rows by ID, so an entry
// without one got no label row and every bar after it shifted onto the next promo's label.
// Gives each such entry an ID of its own and a visible name for display.
export const identifyTimelineEntries = (entries: TimelineEntry[]): TimelineEntry[] =>
  entries.map((entry, index) => ({
    ...entry,
    promoPeriodId: entry.promoPeriodId || `no-promo-period-id-${index}`,
    promoName: entry.promoName || '(No name)'
  }));

export const truncateTimelineEntryDates = (
  entry: TimelineEntry,
  dateMin: Date,
  dateMax: Date
): TimelineEntry => {
  const startDate = lessThanIgnoreTime(entry.startDate, dateMin, false)
    ? Dfns.format('yyyy-MM-dd')(dateMin)
    : entry.startDate;
  const endDate = greaterThanIgnoreTime(entry.endDate, dateMax, false)
    ? Dfns.format('yyyy-MM-dd')(dateMax)
    : entry.endDate;
  return { ...entry, startDate, endDate };
};

export const mergeWithDefaults = (defaultArr: MenuItem[], fetchedArr: MenuItem[]): MenuItem[] => [
  ...defaultArr,
  ...fetchedArr.filter(
    fetchedEntry =>
      fetchedEntry.label &&
      !defaultArr.some(defaultEntry => defaultEntry.label === fetchedEntry.label)
  )
];

export const promoDownload = (timelineEntries: TimelineEntry[], filename: string): void => {
  const exportData = timelineEntries.map(entry =>
    coerceNumericFields(
      {
        'Promo Name': entry.promoName,
        'Special Offer Name': entry.specialOfferName,
        'Percentage Discount': entry.percentageDiscount,
        'Start Date': Dfns.format('yyyy-MM-dd')(Dfns.parseISO(entry.startDate)),
        'End Date': Dfns.format('yyyy-MM-dd')(Dfns.parseISO(entry.endDate))
      },
      ['Percentage Discount']
    )
  );
  exportToExcel(exportData, filename);
};

export const createEmptyKpi = (): IncrementalityTestKpi => ({
  uuid: uuid.v4(),
  type: IncrementalityTestKpiType.VOLUME,
  name: ''
});

export const validateKpis = (
  kpis: IncrementalityTestKpi[],
  revenue: boolean,
  kpiVolume: boolean
): boolean => {
  if (!revenue && !kpiVolume) {
    return false;
  }

  if (!kpis.length) {
    return false;
  }

  const lastKpi = kpiVolume ? kpis.length : 1;
  if (!kpis.slice(0, lastKpi).every(kpi => kpi.uuid && kpi.name && kpi.type)) {
    return false;
  }

  if (revenue && kpis[0].type !== IncrementalityTestKpiType.REVENUE) {
    return false;
  }
  if (kpiVolume) {
    const volumeIdx = revenue ? 1 : 0;
    const volumeKpis = kpis.slice(volumeIdx);
    if (
      volumeKpis.length === 0 ||
      !volumeKpis.every(kpi => kpi.type === IncrementalityTestKpiType.VOLUME)
    ) {
      return false;
    }
  }
  return true;
};

export const DEFAULT_PROMOS: MenuItem[] = [
  {
    id: -1,
    label: 'Black Friday',
    updatedAt: '2025-04-12',
    updatedBy: 'wilson.reyhan@tinuiti.com',
    isDeletable: false
  },
  {
    id: -2,
    label: 'Christmas',
    updatedAt: '2025-04-19',
    updatedBy: 'tim.oh@tinuiti.com',
    isDeletable: false
  },
  {
    id: -3,
    label: 'Memorial Day Weekend',
    updatedAt: '2025-04-26',
    updatedBy: 'alex.grazulis@tinuiti.com',
    isDeletable: false
  }
];

export const DEFAULT_OFFERS: MenuItem[] = [
  {
    id: -1,
    label: 'BOGO',
    updatedAt: '2025-04-12',
    updatedBy: 'wilson.reyhan@tinuiti.com',
    isDeletable: false
  },
  {
    id: -2,
    label: 'Free Shipping',
    updatedAt: '2025-04-19',
    updatedBy: 'tim.oh@tinuiti.com',
    isDeletable: false
  },
  {
    id: -3,
    label: 'Free Gift with Purchase',
    updatedAt: '2025-04-26',
    updatedBy: 'alex.grazulis@tinuiti.com',
    isDeletable: false
  }
];
